import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Icon } from './Icon';
import { IsomerPicker } from './panels/IsomerPicker';
import { ReactionPicker } from './panels/ReactionPicker';
import { IllustrationCanvas } from './IllustrationCanvas';
import { useIllustrationComposer } from '../hooks/useIllustrationComposer';
import type { useMoleculeSearch } from '../hooks/useMoleculeSearch';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';

interface IllustrationComposerProps {
  readonly search: ReturnType<typeof useMoleculeSearch>;
  readonly paperSize: PaperSize;
  readonly onBack: () => void;
}

function describeArmedPick(t: TFunction, armedPick: ReturnType<typeof useIllustrationComposer>['armedPick']): string {
  if (!armedPick) return '';
  if (armedPick.kind === 'isomer') return t('illustrationComposer.armedPick.isomer', { id: armedPick.isomerId });
  if (armedPick.kind === 'molecule') return t('illustrationComposer.armedPick.molecule', { id: armedPick.moleculeId });
  return t('illustrationComposer.armedPick.reaction', { id: armedPick.reactionId });
}

// #140: the Illustration authoring view. Unlike the former ReactionComposer (retired, #705; picking happens in editor mode,
// composing in a separate mode with no picking UI), Illustration's own "pick a reference, then
// click empty canvas space to place it" pattern needs the pickers and the canvas visible together --
// so this is a self-contained view owning pickers + canvas + save, not just a downstream consumer of
// state accumulated elsewhere.
export function IllustrationComposer({ search, paperSize, onBack }: IllustrationComposerProps) {
  const { t } = useTranslation();
  const composer = useIllustrationComposer(paperSize);

  return (
    <section className="reaction-composer illustration-composer" aria-labelledby="illustration-composer-heading">
      <div className="reaction-composer-header">
        <h2 id="illustration-composer-heading">{t('illustrationComposer.heading')}</h2>
        <button
          type="button"
          className="icon-button"
          onClick={onBack}
          aria-label={t('illustrationComposer.backToEditor')}
          title={t('illustrationComposer.backToEditor')}
        >
          <Icon name="arrowBack" />
        </button>
      </div>

      {composer.armedPick ? (
        <p className="illustration-armed-status" role="status">
          {t('illustrationComposer.placingStatus', { description: describeArmedPick(t, composer.armedPick) })}{' '}
          <button
            type="button"
            className="icon-button"
            aria-label={t('illustrationComposer.cancel')}
            title={t('illustrationComposer.cancel')}
            onClick={composer.cancelArmed}
          >
            <Icon name="close" size="1.1rem" />
          </button>
        </p>
      ) : null}

      <div className="illustration-composer-body">
        <div className="illustration-composer-pickers">
          <IsomerPicker
            search={search}
            onSelect={(isomerId, moleculeId, isomerRevision) =>
              composer.armIsomer(isomerId, moleculeId, isomerRevision)
            }
            onSelectMolecule={(moleculeId) => void composer.armMolecule(moleculeId)}
          />
          <ReactionPicker
            onSelect={(reactionId, reactionRevision) => composer.armReaction(reactionId, reactionRevision)}
          />
        </div>

        <IllustrationCanvas
          elements={composer.elements}
          armedPick={composer.armedPick}
          onPlaceAt={composer.placeArmedAt}
          onMoveElement={composer.moveElement}
          onRemoveElement={composer.removeElement}
          onUpdateLabel={composer.updateLabel}
        />
      </div>

      <div className="reaction-composer-actions">
        <button
          type="button"
          className="icon-button"
          disabled={!composer.canSave}
          onClick={() => void composer.save()}
          aria-label={
            composer.saveState.state === 'saving'
              ? t('illustrationComposer.saving')
              : t('illustrationComposer.saveIllustration')
          }
          title={
            composer.saveState.state === 'saving'
              ? t('illustrationComposer.saving')
              : t('illustrationComposer.saveIllustration')
          }
        >
          <Icon name="save" />
        </button>
        {composer.elements.length === 0 ? (
          <p className="reaction-composer-hint">{t('illustrationComposer.minElementsHint')}</p>
        ) : null}
        {composer.saveState.state === 'success' ? (
          <p className="import-success" role="status">
            {composer.saveState.message}
          </p>
        ) : null}
        {composer.saveState.state === 'error' ? (
          <p className="import-error" role="alert">
            {composer.saveState.message}
          </p>
        ) : null}
      </div>
    </section>
  );
}
